import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, useQueries } from "@tanstack/react-query";
import { Button, Card, Badge, Spinner, Alert } from "@vlab-corporation/design-system";
import { DefaultJournalService } from "@vlab-corporation/vlab-pilates-default-service/class";
import type {
  ClassType,
  EquipmentType,
  ExerciseEntry,
} from "@vlab-corporation/vlab-pilates-service-interface/class";
import { normalizeClassFormat } from "@vlab-corporation/vlab-pilates-service-interface/branch";
import { detectClassType, detectIsOt } from "@vlab-corporation/vlab-pilates-default-service/class";
import { AppLayout } from "@/components/AppLayout";
import { ClassTypeBadge } from "@/components/ClassFormatSelector";
import {
  EquipmentExerciseSection,
  migrateExercisesWithEquipment,
} from "@/components/EquipmentExerciseSection";
import { MemberTypeBadge } from "@/components/MemberTypeBadge";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useSyncMembers } from "@/hooks/useSyncMembers";
import { useStudiomateSchedules } from "@/hooks/useStudiomateSchedules";
import { useStudiomateLectureDetail } from "@/hooks/useStudiomateLectureDetail";
import {
  getJournalByClassId,
  getJournalsByClassId,
  getJournalsByMemberId,
  saveJournal,
  saveJournalWithOtDemotion,
  updateJournal,
  batchSaveGroupJournals,
} from "@/services/firestore/journals";
import {
  getClassCompletion,
  updateClassCompletion,
  updateGroupClassJournalWritten,
} from "@/services/firestore/classCompletions";
import { useToast } from "@/components/ToastProvider";
import { getBranchPolicy } from "@/services/firestore/branchPolicy";
import { getInstructor, getInstructorByStaffId } from "@/services/firestore/instructors";
import { getMovements } from "@/services/firestore/movements";
import { getReRegistrations } from "@/services/firestore/members";
import { sameStaffId, resolveMyStaffKey } from "@/utils/staff-identity";
import { findConflictingOtJournals } from "@/utils/ot-journal";
import { useJournalDraft } from "@/hooks/useJournalDraft";
import { useGroupJournalForm } from "@/hooks/useGroupJournalForm";
import type { ToggleField, GroupMember } from "@/hooks/group-journal-utils";
import { ToggleableFieldSection } from "@/components/ToggleableFieldSection";
import { JournalHistoryDrawer } from "@/components/JournalHistoryDrawer";
import { PastProblemListPicker } from "@/components/PastProblemListPicker";
import { useJournalHistory } from "@/hooks/useJournalHistory";
import { trackEvent, EVENT_NAMES } from "@/utils/analytics-tracking";

const journalService = new DefaultJournalService();

/** 일지 createdAt(ISO 문자열 또는 Firestore Timestamp)을 "YYYY년 M월 D일"로 표기. */
function formatJournalDate(createdAt: unknown): string {
  let ms = 0;
  if (typeof createdAt === "string") ms = new Date(createdAt).getTime();
  else if (createdAt && typeof createdAt === "object" && "toDate" in createdAt)
    ms = (createdAt as { toDate(): Date }).toDate().getTime();
  else if (createdAt && typeof createdAt === "object" && "seconds" in createdAt)
    ms = (createdAt as { seconds: number }).seconds * 1000;
  if (!ms || Number.isNaN(ms)) return "이전";
  const d = new Date(ms);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;
}

export function isAllNoShow(bookings: Array<{ memberName: string; status: string }>): boolean {
  return bookings.length > 0 && bookings.every((b) => b.status === "absence");
}

/** 토글 필드 한글 라벨 매핑 */
const FIELD_LABELS: Record<ToggleField, string> = {
  problemList: "문제 목록(Problem List)",
  notes: "특이사항",
  improvements: "개선된 점",
  improvementsNeeded: "개선해야할 점",
  bodyAnalysis: "정·동적 평가",
  memberGoal: "회원 운동 목표",
  instructorMemo: "상담 내용(문진)",
};

const FIELD_PLACEHOLDERS: Record<ToggleField, string> = {
  problemList: "고객이 겪는 불편함의 핵심 원인은 무엇이라고 생각하는지를 기록해주세요.",
  notes: "통증, 컨디션, 다음 계획...",
  improvements: "개선된 점을 기록해주세요...",
  improvementsNeeded: "개선해야할 점을 기록해주세요...",
  bodyAnalysis: "정적·동적 평가 내용...",
  memberGoal: "회원이 원하는 운동 목표...",
  instructorMemo: "상담 및 문진 내용...",
};

/** 필수 토글 필드 (notes는 선택) */
const REQUIRED_TOGGLE_FIELDS: ToggleField[] = ["problemList", "improvements", "improvementsNeeded"];
const REQUIRED_OT_TOGGLE_FIELDS: ToggleField[] = ["bodyAnalysis", "memberGoal", "instructorMemo"];

export function JournalPage() {
  const { classId } = useParams<{ classId: string }>();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date");
  const fromParam = searchParams.get("from");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthenticatedUser();
  const toast = useToast();

  // 저장 후 돌아갈 경로: from이 /로 시작하는 유효 경로면 사용, 아니면 기본값
  const returnPath = fromParam && fromParam.startsWith("/") ? fromParam : "/pending-journals";

  useEffect(() => {
    if (classId) {
      trackEvent(EVENT_NAMES.JOURNAL_STARTED, { class_id: classId });
    }
  }, [classId]);

  const [classType, setClassType] = useState<ClassType | undefined>(undefined);
  const [isOt, setIsOt] = useState(false);
  const [pendingDemotionIds, setPendingDemotionIds] = useState<string[]>([]);
  const [otPrompt, setOtPrompt] = useState<
    | {
        kind: "demote";
        conflictIds: string[];
        dateLabel: string;
        instructorName: string;
        memberName: string;
      }
    | { kind: "lastOff"; memberName: string }
    | null
  >(null);
  const [equipments, setEquipments] = useState<EquipmentType[]>([]);
  const [exercises, setExercises] = useState<ExerciseEntry[]>([]);
  const [problemList, setProblemList] = useState("");
  const [improvements, setImprovements] = useState("");
  const [improvementsNeeded, setImprovementsNeeded] = useState("");
  const [notes, setNotes] = useState("");
  const [bodyAnalysis, setBodyAnalysis] = useState("");
  const [memberGoal, setMemberGoal] = useState("");
  const [instructorMemo, setInstructorMemo] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [dismissNoShow, setDismissNoShow] = useState(false);
  const [showNoShowConfirm, setShowNoShowConfirm] = useState(false);
  const [showCancelNoShowConfirm, setShowCancelNoShowConfirm] = useState(false);
  const [draftSaveSuccess, setDraftSaveSuccess] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{
    studiomateMemberId: number;
    memberName: string;
  } | null>(null);

  const centerOwnerUid = user?.activeMembership.centerOwnerUid ?? "";
  const centerId = user?.activeMembership.centerId ?? 0;
  const myInstructorId = user?.activeMembership.instructorId ?? "";
  // 강사 식별자: 매핑되어 있으면 studiomateStaffId, 아니면 instructorId(=userUid).
  const myStaffKey: number | string | undefined = user
    ? resolveMyStaffKey(user.activeMembership)
    : undefined;
  const enabled = !!user && !!classId;

  const { saveDraft, loadDraft, clearDraft } = useJournalDraft(centerId, classId);

  // 마운트 시 Studiomate 전체 회원 동기화 후 최신 회원 목록 반환
  const { membersMap, isLoading: membersLoading } = useSyncMembers(centerOwnerUid, centerId);

  // 재등록 관계 맵 로드
  const { data: reRegistrations } = useQuery({
    queryKey: ["reRegistrations", centerOwnerUid, centerId],
    queryFn: () => getReRegistrations(centerOwnerUid, centerId),
    staleTime: 30 * 60 * 1000,
    enabled: !!centerOwnerUid && !!centerId,
  });

  // Fetch schedule data for no-show detection and lectureType determination
  const { data: scheduleData } = useStudiomateSchedules({ date: dateParam ?? "" }, centerId);

  // Lecture detail API로 studiomateMemberId를 확실하게 확보
  const { data: lectureDetail } = useStudiomateLectureDetail(classId ? Number(classId) : undefined);

  // 수업 담당 강사의 staffId 판별
  const classStaffId = useMemo(() => {
    if (!scheduleData?.schedules || !classId) return undefined;
    const schedule = scheduleData.schedules.find((s) => String(s.id) === classId);
    return schedule?.staffId;
  }, [scheduleData, classId]);

  // 대리작성 모드: 수업 담당 강사 ≠ 로그인 사용자
  const isProxyWrite =
    classStaffId !== undefined &&
    myStaffKey !== undefined &&
    !sameStaffId(classStaffId, myStaffKey);

  // 대리작성 시 수업 담당 강사의 instructorId를 조회
  const { data: classInstructor, isLoading: classInstructorLoading } = useQuery({
    queryKey: ["instructorByStaffId", centerOwnerUid, centerId, classStaffId],
    queryFn: () => getInstructorByStaffId(centerOwnerUid, centerId, classStaffId!),
    enabled: isProxyWrite && !!classStaffId,
    staleTime: 30 * 60 * 1000,
  });

  // 대리작성 매핑 실패: 수업 담당 강사가 Firestore에 매핑되지 않은 경우
  const proxyMappingFailed = isProxyWrite && !classInstructorLoading && !classInstructor;

  // 유효한 instructorId: 대리작성이면 수업 담당 강사, 아니면 본인
  const instructorId = isProxyWrite && classInstructor ? classInstructor.id : myInstructorId;

  // 스케줄 데이터에서 현재 수업의 lectureType 판별 ("P": 개인, "G": 그룹)
  const lectureType = useMemo(() => {
    if (!scheduleData?.schedules || !classId) return undefined;
    const schedule = scheduleData.schedules.find((s) => String(s.id) === classId);
    return schedule?.type;
  }, [scheduleData, classId]);

  const scheduleStaffName = useMemo(() => {
    if (!scheduleData?.schedules || !classId) return "담당 강사";
    const schedule = scheduleData.schedules.find((s) => String(s.id) === classId);
    return schedule?.staffName ?? "담당 강사";
  }, [scheduleData, classId]);

  // 그룹 판별: maxTrainees 기준 (Studiomate type 필드는 semi-private 등에서 부정확할 수 있음)
  const isGroupClass = useMemo(() => {
    // 1. 기존 일지의 classType에서 판별
    if (classType === "GROUP_REGULAR") return true;
    if (classType === "PRIVATE_REGULAR") return false;
    // 2. 스케줄 데이터의 maxTrainees에서 판별 (detectClassType과 동일 로직)
    if (!scheduleData?.schedules || !classId) return false;
    const schedule = scheduleData.schedules.find((s) => String(s.id) === classId);
    if (!schedule) return false;
    return schedule.maxTrainees >= 2;
  }, [classType, scheduleData, classId]);

  // 그룹 수업 회원 목록 (cancel 제외)
  const groupMembers = useMemo<GroupMember[]>(() => {
    if (!isGroupClass || !classId) return [];
    // lectureDetail 우선 (studiomateMemberId 보장)
    if (lectureDetail?.bookings) {
      return lectureDetail.bookings
        .filter((b) => b.status !== "cancel")
        .map((b) => ({
          studiomateMemberId: b.studiomateMemberId,
          memberName: b.memberName,
        }));
    }
    // scheduleData fallback
    const schedule = scheduleData?.schedules?.find((s) => String(s.id) === classId);
    if (!schedule) return [];
    return schedule.bookings
      .filter((b) => b.status !== "cancel" && b.studiomateMemberId !== undefined)
      .map((b) => ({
        studiomateMemberId: b.studiomateMemberId!,
        memberName: b.memberName,
      }));
  }, [isGroupClass, classId, lectureDetail, scheduleData]);

  // 개인 수업: 기존 일지 1개 조회
  const { data: existingJournal, isLoading: journalLoading } = useQuery({
    queryKey: ["journal", centerId, classId],
    queryFn: () => getJournalByClassId(centerOwnerUid, centerId, classId!, instructorId),
    enabled: enabled && !isGroupClass,
  });

  // 그룹 수업: 모든 회원 일지 조회 (초기화 + 저장 판별용)
  const { data: existingGroupJournals, isLoading: groupJournalsLoading } = useQuery({
    queryKey: ["groupJournals", centerOwnerUid, centerId, classId],
    queryFn: () => getJournalsByClassId(centerOwnerUid, centerId, classId!),
    enabled: enabled && isGroupClass,
    staleTime: 5 * 60 * 1000,
  });

  const isJournalLoading = isGroupClass ? groupJournalsLoading : journalLoading;
  const hasExistingData = isGroupClass
    ? (existingGroupJournals?.length ?? 0) > 0
    : !!existingJournal;

  // 그룹 일지 폼 hook
  const groupForm = useGroupJournalForm({
    members: groupMembers,
    existingJournals: existingGroupJournals ?? [],
    isOt,
  });

  // studiomateMemberId 해석 (개인 수업용)
  const resolvedStudiomateMemberId = useMemo(() => {
    if (isGroupClass) return undefined; // 그룹은 batch save에서 처리
    // 1. lectureDetail
    if (lectureDetail?.bookings && lectureDetail.bookings.length > 0 && lectureType === "P") {
      return lectureDetail.bookings[0].studiomateMemberId;
    }
    // 2. scheduleData
    if (scheduleData?.schedules && classId) {
      const schedule = scheduleData.schedules.find((s) => String(s.id) === classId);
      if (schedule && lectureType === "P") {
        const activeBooking = schedule.bookings.find((b) => b.status !== "cancel");
        if (activeBooking?.studiomateMemberId) return activeBooking.studiomateMemberId;
      }
    }
    // 3. existingJournal
    if (existingJournal?.studiomateMemberId) return existingJournal.studiomateMemberId;
    return undefined;
  }, [isGroupClass, lectureDetail, scheduleData, classId, lectureType, existingJournal]);

  // 개인 수업 수강생 이름 (이력 드로어 표시용)
  const privateMemberName = useMemo(() => {
    if (isGroupClass) return undefined;
    if (lectureDetail?.bookings && lectureDetail.bookings.length > 0) {
      return lectureDetail.bookings[0].memberName;
    }
    if (scheduleData?.schedules && classId) {
      const schedule = scheduleData.schedules.find((s) => String(s.id) === classId);
      const activeBooking = schedule?.bookings.find((b) => b.status !== "cancel");
      return activeBooking?.memberName;
    }
    return undefined;
  }, [isGroupClass, lectureDetail, scheduleData, classId]);

  // 개인 수업: 페이지 진입 즉시 이력 prefetch (드로어/Picker가 캐시 공유)
  useJournalHistory(
    centerOwnerUid,
    centerId,
    !isGroupClass ? resolvedStudiomateMemberId : undefined,
    privateMemberName,
  );

  // 그룹 수업: 전체 회원 이력 동시 prefetch
  useQueries({
    queries: isGroupClass
      ? groupMembers.map((m) => ({
          queryKey: ["journalHistory", centerOwnerUid, centerId, m.studiomateMemberId],
          queryFn: () =>
            getJournalsByMemberId(centerOwnerUid, centerId, m.studiomateMemberId, m.memberName),
          enabled: !!centerOwnerUid && !!centerId,
        }))
      : [],
  });

  const { data: branchPolicy } = useQuery({
    queryKey: ["branchPolicy", centerId],
    queryFn: () => getBranchPolicy(centerOwnerUid, centerId),
    enabled,
  });

  const { data: instructorDoc } = useQuery({
    queryKey: ["instructor", centerId, instructorId],
    queryFn: () => getInstructor(centerOwnerUid, centerId, instructorId),
    staleTime: 30 * 60 * 1000,
    enabled,
  });

  const { data: allMovements } = useQuery({
    queryKey: ["movements", centerId],
    queryFn: () => getMovements(centerOwnerUid, centerId),
    staleTime: 1000 * 60 * 30,
    enabled,
  });

  const { data: classCompletion } = useQuery({
    queryKey: ["classCompletion", centerId, classId],
    queryFn: () => getClassCompletion(centerOwnerUid, centerId, classId!),
    enabled,
  });

  const currentIsNoShow = classCompletion?.isNoShow === true;

  // Detect no-show
  const noShowDetected = useMemo(() => {
    if (
      !dateParam ||
      !scheduleData?.schedules ||
      !classId ||
      hasExistingData ||
      dismissNoShow ||
      currentIsNoShow
    ) {
      return false;
    }
    const schedule = scheduleData.schedules.find((s) => String(s.id) === classId);
    if (!schedule) return false;
    return isAllNoShow(schedule.bookings);
  }, [dateParam, scheduleData, classId, hasExistingData, dismissNoShow, currentIsNoShow]);

  /** 현재 수업에 맞는 정산 위탁대금 (시니어 여부 + classType + isOt 기반 필터) */
  const payScaleInfo = useMemo(() => {
    const seniorSince = instructorDoc?.seniorSince;
    const classDate = dateParam ?? undefined;
    const isSenior =
      seniorSince != null &&
      branchPolicy?.seniorInstructorPayScale != null &&
      branchPolicy.seniorInstructorPayScale.length > 0 &&
      (classDate == null || classDate >= seniorSince);

    const payScale = isSenior
      ? branchPolicy!.seniorInstructorPayScale!
      : branchPolicy?.instructorPayScale;
    if (!payScale || payScale.length === 0) return null;

    const effectiveClassType =
      classType === "GROUP_REGULAR"
        ? "GROUP_REGULAR"
        : classType === "PRIVATE_REGULAR"
          ? "PRIVATE_REGULAR"
          : isGroupClass
            ? "GROUP_REGULAR"
            : "PRIVATE_REGULAR";
    const effectiveIsOt = isOt ?? false;

    const resolveClassType = (p: { classType?: ClassType; classFormat?: ClassType }) =>
      p.classType ?? normalizeClassFormat(p.classFormat);

    let items = payScale.filter(
      (p) => resolveClassType(p) === effectiveClassType && (p.isOt ?? false) === effectiveIsOt,
    );
    if (items.length === 0) {
      items = payScale.filter((p) => resolveClassType(p) === effectiveClassType);
    }
    if (items.length === 0) {
      items = payScale.filter(
        (p) => resolveClassType(p) === "PRIVATE_REGULAR" && (p.isOt ?? false) === effectiveIsOt,
      );
    }
    if (items.length === 0) return null;

    const fp = items.find((p) => p.memberType === "firstPurchase");
    const rr = items.find((p) => p.memberType === "reRegistration");

    const samePrices = fp && rr && fp.unitPrice === rr.unitPrice;
    const priceLabel = samePrices
      ? `₩${fp.unitPrice.toLocaleString()}`
      : [
          fp && `신규 ₩${fp.unitPrice.toLocaleString()}`,
          rr && `재등록 ₩${rr.unitPrice.toLocaleString()}`,
        ]
          .filter(Boolean)
          .join(" / ");

    return { isSenior, priceLabel };
  }, [branchPolicy, classType, isOt, isGroupClass, instructorDoc, dateParam]);

  // Initialize form from existing journal (개인 수업)
  useEffect(() => {
    if (isGroupClass || !existingJournal) return;

    setClassType(existingJournal.classType);
    setIsOt(existingJournal.isOt ?? false);
    setEquipments(existingJournal.equipments ?? []);
    setProblemList(existingJournal.problemList ?? "");
    setImprovements(existingJournal.improvements ?? "");
    setImprovementsNeeded(existingJournal.improvementsNeeded ?? "");
    setNotes(existingJournal.notes ?? "");
    setBodyAnalysis(existingJournal.bodyAnalysis ?? "");
    setMemberGoal(existingJournal.memberGoal ?? "");
    setInstructorMemo(existingJournal.instructorMemo ?? "");

    const loadedEquipments = existingJournal.equipments ?? [];
    if (existingJournal.exercises && existingJournal.exercises.length > 0) {
      setExercises(migrateExercisesWithEquipment(existingJournal.exercises, loadedEquipments));
    } else if (existingJournal.movements && existingJournal.movements.length > 0) {
      const converted = existingJournal.movements.map((m) => ({
        name: allMovements?.find((am) => am.id === m.movementId)?.name ?? m.movementId,
        movementId: m.movementId,
      }));
      setExercises(migrateExercisesWithEquipment(converted, loadedEquipments));
    }
  }, [isGroupClass, existingJournal, allMovements]);

  // Initialize from existing group journals (그룹 수업: 공통 필드만)
  useEffect(() => {
    if (!isGroupClass || !existingGroupJournals || existingGroupJournals.length === 0) return;
    const first = existingGroupJournals[0];
    setClassType(first.classType);
    setIsOt(first.isOt ?? false);
    const groupEquipments = first.equipments ?? [];
    setEquipments(groupEquipments);
    if (first.exercises && first.exercises.length > 0) {
      setExercises(migrateExercisesWithEquipment(first.exercises, groupEquipments));
    } else if (first.movements && first.movements.length > 0) {
      const converted = first.movements.map((m) => ({
        name: allMovements?.find((am) => am.id === m.movementId)?.name ?? m.movementId,
        movementId: m.movementId,
      }));
      setExercises(migrateExercisesWithEquipment(converted, groupEquipments));
    }
  }, [isGroupClass, existingGroupJournals, allMovements]);

  const seededRef = useRef(false);
  // Auto-determine classType and isOt from schedule data (기존 일지가 없을 때, 1회만)
  useEffect(() => {
    if (hasExistingData) return;
    if (seededRef.current) return;
    if (!scheduleData?.schedules || !classId) return;
    const schedule = scheduleData.schedules.find((s) => String(s.id) === classId);
    if (!schedule) return;
    setClassType(detectClassType(schedule.maxTrainees));
    setIsOt(detectIsOt(schedule.title));
    seededRef.current = true;
  }, [scheduleData, classId, hasExistingData]);

  // Load draft when no existing journal (개인 수업만)
  useEffect(() => {
    if (isGroupClass) return;
    if (journalLoading) return;
    if (existingJournal) return;
    const draft = loadDraft();
    if (!draft) return;
    setClassType(draft.classType);
    setIsOt(draft.isOt ?? false);
    setEquipments(draft.equipments);
    setExercises(draft.exercises);
    setProblemList(draft.problemList);
    setImprovements(draft.improvements ?? "");
    setImprovementsNeeded(draft.improvementsNeeded ?? "");
    setNotes(draft.notes);
    setBodyAnalysis(draft.bodyAnalysis);
    setMemberGoal(draft.memberGoal);
    setInstructorMemo(draft.instructorMemo);
    setDraftLoaded(true);
  }, [isGroupClass, journalLoading, existingJournal]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Validation ---

  const isOtFieldsValid =
    !isOt ||
    (isGroupClass
      ? groupForm.validate(REQUIRED_OT_TOGGLE_FIELDS).length === 0
      : instructorMemo.trim().length > 0 &&
        bodyAnalysis.trim().length > 0 &&
        memberGoal.trim().length > 0);

  const isFormValid = useMemo(() => {
    const baseValid =
      classType !== undefined && equipments.length > 0 && exercises.length > 0 && isOtFieldsValid;

    if (!baseValid) return false;

    if (isGroupClass) {
      // 그룹 수업: 토글 필드 검증
      const requiredFields: ToggleField[] = [...REQUIRED_TOGGLE_FIELDS];
      if (isOt) requiredFields.push(...REQUIRED_OT_TOGGLE_FIELDS);
      return groupForm.validate(requiredFields).length === 0;
    }

    // 개인 수업: 기존 검증
    return (
      problemList.trim().length > 0 &&
      improvements.trim().length > 0 &&
      improvementsNeeded.trim().length > 0
    );
  }, [
    classType,
    equipments,
    exercises,
    isOtFieldsValid,
    isGroupClass,
    isOt,
    groupForm,
    problemList,
    improvements,
    improvementsNeeded,
  ]);

  const missingFields: string[] = [];
  if (!classType) missingFields.push("수업 유형");
  if (equipments.length === 0) missingFields.push("사용 기구");
  if (exercises.length === 0) missingFields.push("수업 시퀀스");
  if (isGroupClass) {
    const requiredFields: ToggleField[] = [...REQUIRED_TOGGLE_FIELDS];
    if (isOt) requiredFields.push(...REQUIRED_OT_TOGGLE_FIELDS);
    const invalidFields = groupForm.validate(requiredFields);
    for (const f of invalidFields) missingFields.push(FIELD_LABELS[f]);
  } else {
    if (!problemList.trim()) missingFields.push("문제 목록");
    if (!improvements.trim()) missingFields.push("개선된 점");
    if (!improvementsNeeded.trim()) missingFields.push("개선해야할 점");
    if (isOt) {
      if (!instructorMemo.trim()) missingFields.push("상담 내용(문진)");
      if (!bodyAnalysis.trim()) missingFields.push("정·동적 평가");
      if (!memberGoal.trim()) missingFields.push("회원 운동 목표");
    }
  }

  const fieldToSectionId: Record<string, string> = {
    "수업 유형": "section-class-type",
    "상담 내용(문진)": "section-ot-fields",
    "정·동적 평가": "section-ot-fields",
    "회원 운동 목표": "section-ot-fields",
    "사용 기구": "section-equipment-exercises",
    "수업 시퀀스": "section-equipment-exercises",
    "문제 목록": "section-problem-list",
    "문제 목록(Problem List)": "section-problem-list",
    "개선된 점": "section-improvements",
    "개선해야할 점": "section-improvements-needed",
  };

  function scrollToFirstMissing() {
    if (missingFields.length === 0) return;
    const sectionId = fieldToSectionId[missingFields[0]];
    if (sectionId) {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  function handleDraftSave() {
    saveDraft({
      classType,
      isOt,
      equipments,
      exercises,
      problemList,
      improvements,
      improvementsNeeded,
      notes,
      bodyAnalysis,
      memberGoal,
      instructorMemo,
    });
    setDraftSaveSuccess(true);
  }

  // OT 토글(private만 검사): ON은 같은 강사-회원의 기존 OT가 있으면 강등 확인 팝업,
  // OFF는 다른 OT가 없어(이 회원의 OT가 0개가 됨) 경고 팝업. 그룹·회원 미식별은 즉시 반영.
  async function handleOtToggle(checked: boolean) {
    if (isGroupClass || resolvedStudiomateMemberId === undefined) {
      setIsOt(checked);
      setPendingDemotionIds([]);
      return;
    }
    try {
      const journals = await getJournalsByMemberId(
        centerOwnerUid,
        centerId,
        resolvedStudiomateMemberId,
        privateMemberName,
      );
      // 현재 수업을 제외한, 같은 강사-회원의 다른 OT 일지들.
      const otherOt = findConflictingOtJournals(journals, instructorId, classId ?? "");
      if (checked) {
        if (otherOt.length === 0) {
          setIsOt(true);
          return;
        }
        setOtPrompt({
          kind: "demote",
          conflictIds: otherOt.map((j) => j.id),
          dateLabel: formatJournalDate(otherOt[0].createdAt),
          instructorName: scheduleStaffName,
          memberName: privateMemberName ?? "회원",
        });
      } else {
        if (otherOt.length > 0) {
          setIsOt(false);
          setPendingDemotionIds([]);
          return;
        }
        setOtPrompt({ kind: "lastOff", memberName: privateMemberName ?? "회원" });
      }
    } catch {
      // 조회 실패: 판정 불가 → 토글을 직전 상태로 되돌린다(되돌리기 안전) + 안내.
      // pendingDemotionIds는 건드리지 않는다 — OFF 조회 실패로 ON으로 되돌릴 때
      // 직전에 확정된 강등 예정이 유지돼야 저장 시 중복 OT가 생기지 않는다.
      toast.show("OT 중복 확인에 실패했습니다.", "error");
      setIsOt(!checked);
      setOtPrompt(null);
    }
  }

  function confirmOtPrompt() {
    if (!otPrompt) return;
    if (otPrompt.kind === "demote") {
      setIsOt(true);
      setPendingDemotionIds(otPrompt.conflictIds);
    } else {
      setIsOt(false);
      setPendingDemotionIds([]);
    }
    setOtPrompt(null);
  }

  function cancelOtPrompt() {
    // 취소: isOt는 현재 상태 유지(demote면 off 유지, lastOff면 on 유지).
    setOtPrompt(null);
  }

  // --- Save mutations ---

  // 개인 수업 저장
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!classId) throw new Error("classId is required");

      journalService.createJournal({
        journalId: existingJournal?.id ?? "temp",
        classId,
        instructorId,
        classType,
        equipments,
        exercises,
        problemList,
        improvements,
        improvementsNeeded,
        createdAt: new Date().toISOString(),
        ...(notes ? { notes } : {}),
        ...(bodyAnalysis ? { bodyAnalysis } : {}),
        ...(memberGoal ? { memberGoal } : {}),
        ...(instructorMemo ? { instructorMemo } : {}),
      });

      const journalData = {
        classId,
        instructorId,
        ...(resolvedStudiomateMemberId !== undefined
          ? { studiomateMemberId: resolvedStudiomateMemberId }
          : {}),
        classType,
        isOt,
        equipments,
        exercises,
        problemList,
        improvements,
        improvementsNeeded,
        ...(notes ? { notes } : {}),
        ...(bodyAnalysis ? { bodyAnalysis } : {}),
        ...(memberGoal ? { memberGoal } : {}),
        ...(instructorMemo ? { instructorMemo } : {}),
      };

      let journalId: string;
      if (isOt && pendingDemotionIds.length > 0 && resolvedStudiomateMemberId !== undefined) {
        // 저장 직전 재확인(레이스 방지): 여전히 충돌하는 OT만 강등.
        const fresh = await getJournalsByMemberId(
          centerOwnerUid,
          centerId,
          resolvedStudiomateMemberId,
          privateMemberName,
        );
        const demoteIds = findConflictingOtJournals(fresh, instructorId, classId).map((j) => j.id);
        journalId = await saveJournalWithOtDemotion(centerOwnerUid, centerId, {
          existingJournalId: existingJournal?.id ?? null,
          data: journalData,
          demoteJournalIds: demoteIds,
        });
      } else if (existingJournal) {
        await updateJournal(centerOwnerUid, centerId, existingJournal.id, journalData);
        journalId = existingJournal.id;
      } else {
        journalId = await saveJournal(centerOwnerUid, centerId, journalData);
      }

      await updateClassCompletion(centerOwnerUid, centerId, classId, true, journalId);
      return journalId;
    },
    onSuccess: () => {
      trackEvent(EVENT_NAMES.JOURNAL_COMPLETED, { class_id: classId ?? "" });
      clearDraft();
      setSaveError(null);
      setPendingDemotionIds([]);
      queryClient.invalidateQueries({ queryKey: ["journal", centerId, classId] });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      queryClient.invalidateQueries({ queryKey: ["classCompletions-batch"] });
      queryClient.invalidateQueries({ queryKey: ["pending-journals"] });
      toast.show("일지가 저장되었습니다.", "success");
      navigate(returnPath, { replace: true });
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : "저장에 실패했습니다.");
    },
  });

  // 그룹 수업 일괄 저장
  const groupSaveMutation = useMutation({
    mutationFn: async () => {
      if (!classId) throw new Error("classId is required");

      const docs = groupForm.buildDocuments({
        classId,
        instructorId,
        classType,
        isOt,
        equipments,
        exercises,
      });

      const journalIds = await batchSaveGroupJournals(
        centerOwnerUid,
        centerId,
        existingGroupJournals ?? [],
        docs,
      );

      // 전체 회원 일지가 한번에 생성 → journalWritten = true
      const schedule = scheduleData?.schedules?.find((s) => String(s.id) === classId);
      const activeTrainees = schedule?.bookings.filter((b) => b.status !== "cancel").length ?? 0;
      await updateGroupClassJournalWritten(
        centerOwnerUid,
        centerId,
        classId,
        journalIds.length,
        activeTrainees,
      );

      return journalIds;
    },
    onSuccess: () => {
      trackEvent(EVENT_NAMES.JOURNAL_COMPLETED, { class_id: classId ?? "" });
      setSaveError(null);
      queryClient.invalidateQueries({
        queryKey: ["groupJournals", centerOwnerUid, centerId, classId],
      });
      queryClient.invalidateQueries({ queryKey: ["classes"] });
      queryClient.invalidateQueries({ queryKey: ["classCompletions-batch"] });
      queryClient.invalidateQueries({ queryKey: ["pending-journals"] });
      toast.show("일지가 저장되었습니다.", "success");
      navigate(returnPath, { replace: true });
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : "저장에 실패했습니다.");
    },
  });

  const noShowMutation = useMutation({
    mutationFn: async () => {
      if (!classId) throw new Error("classId is required");
      await updateClassCompletion(
        centerOwnerUid,
        centerId,
        classId,
        true,
        existingJournal?.id,
        true,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classCompletions-batch"] });
      queryClient.invalidateQueries({ queryKey: ["pending-journals"] });
      navigate(-1);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : "노쇼 처리에 실패했습니다.");
    },
  });

  const cancelNoShowMutation = useMutation({
    mutationFn: async () => {
      if (!classId) throw new Error("classId is required");
      const journalWritten = isGroupClass
        ? (existingGroupJournals?.length ?? 0) > 0
        : !!existingJournal;
      await updateClassCompletion(
        centerOwnerUid,
        centerId,
        classId,
        journalWritten,
        existingJournal?.id,
        false,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classCompletion"] });
      queryClient.invalidateQueries({ queryKey: ["classCompletions-batch"] });
      queryClient.invalidateQueries({ queryKey: ["pending-journals"] });
      navigate(-1);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : "노쇼 취소에 실패했습니다.");
    },
  });

  const isSaving = isGroupClass ? groupSaveMutation.isPending : saveMutation.isPending;

  function handleSave() {
    if (!isFormValid) {
      setSaveError(`미입력 항목이 있습니다: ${missingFields.join(", ")}`);
      scrollToFirstMissing();
      return;
    }
    if (isGroupClass) {
      groupSaveMutation.mutate();
    } else {
      saveMutation.mutate();
    }
  }

  // --- Render ---

  if (!user || !classId || isJournalLoading || membersLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  // 수업 일지 작성 시작일 이전 수업 차단
  const journalWritingStartDate = branchPolicy?.journalWritingStartDate;
  if (journalWritingStartDate && dateParam && dateParam < journalWritingStartDate) {
    return (
      <AppLayout>
        <div className="mb-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            ← 뒤로
          </Button>
        </div>
        <Alert variant="info">
          수업 일지 작성 시작일({journalWritingStartDate}) 이전의 수업은 일지 작성이 불가합니다.
        </Alert>
      </AppLayout>
    );
  }

  // 대리작성 시 수업 담당 강사 매핑 실패
  if (proxyMappingFailed) {
    return (
      <AppLayout>
        <div className="mb-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            ← 뒤로
          </Button>
        </div>
        <Alert variant="warning">
          이 수업의 담당 강사가 Studiomate에 매핑되어 있지 않아 대리 작성이 불가합니다.
        </Alert>
      </AppLayout>
    );
  }

  // No-show detection card
  if (noShowDetected) {
    return (
      <AppLayout>
        <div className="mb-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            ← 뒤로
          </Button>
        </div>

        <Card className="p-4 mb-4">
          <h2 className="text-lg font-semibold mb-2">
            {hasExistingData ? "일지 편집" : "일지 작성"}
          </h2>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>수업 ID: {classId}</span>
          </div>
        </Card>

        <Alert variant="warning" className="mb-4">
          전원 노쇼가 감지되었습니다
        </Alert>

        <Card className="p-4 mb-4">
          <p className="text-sm text-gray-600 mb-4">
            이 수업의 모든 수강생이 결석(노쇼) 상태입니다. 노쇼로 처리하면 일지 작성 없이 완료
            처리됩니다.
          </p>
          <div className="flex gap-3">
            <Button
              variant="primary"
              onClick={() => noShowMutation.mutate()}
              disabled={noShowMutation.isPending}
              className="flex-1"
            >
              {noShowMutation.isPending ? "처리 중..." : "노쇼로 처리"}
            </Button>
            <Button variant="outline" onClick={() => setDismissNoShow(true)} className="flex-1">
              일지 직접 작성
            </Button>
          </div>
        </Card>

        {saveError && (
          <Alert variant="error" className="mb-4">
            {saveError}
          </Alert>
        )}
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mb-4">
        <Button variant="ghost" onClick={() => navigate(returnPath)}>
          ← 뒤로
        </Button>
      </div>

      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">{hasExistingData ? "일지 편집" : "일지 작성"}</h2>
          {!isGroupClass && resolvedStudiomateMemberId !== undefined && (
            <button
              type="button"
              className="text-xs font-semibold px-3 py-1 rounded-full border border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white transition-colors"
              onClick={() =>
                setHistoryTarget({
                  studiomateMemberId: resolvedStudiomateMemberId,
                  memberName: privateMemberName ?? "",
                })
              }
            >
              이력 보기
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>수업 ID: {classId}</span>
          {payScaleInfo && (
            <span className="inline-flex items-center gap-1.5 text-sm">
              {payScaleInfo.isSenior && (
                <span className="inline-flex items-center font-medium bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                  시니어
                </span>
              )}
              <Badge variant="info">정산 위탁대금: {payScaleInfo.priceLabel}</Badge>
            </span>
          )}
        </div>
        {/* 수강생 목록 표시 */}
        {(() => {
          const schedule = scheduleData?.schedules?.find((s) => String(s.id) === classId);
          if (!schedule) return null;
          const activeBookings = schedule.bookings.filter((b) => b.status !== "cancel");
          if (activeBookings.length === 0) return null;
          return (
            <div className="mt-3 space-y-1">
              <p className="text-xs text-gray-500 font-medium">
                수강생{" "}
                {isGroupClass && (
                  <Badge variant="info" className="ml-1">
                    {activeBookings.length}명
                  </Badge>
                )}
              </p>
              {activeBookings.map((b, idx) => {
                const member =
                  b.studiomateMemberId != null
                    ? membersMap?.get(String(b.studiomateMemberId))
                    : membersMap?.get(b.memberName);
                const groupMember = isGroupClass
                  ? groupMembers.find((m) => m.memberName === b.memberName)
                  : undefined;
                return (
                  <div key={idx} className="flex items-center gap-1 text-xs text-gray-700">
                    <span>{b.memberName}</span>
                    <MemberTypeBadge
                      member={member}
                      classDate={dateParam ?? undefined}
                      instructorId={instructorId}
                      reRegistrations={reRegistrations}
                    />
                    {groupMember && (
                      <button
                        type="button"
                        className="ml-auto shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white transition-colors"
                        onClick={() =>
                          setHistoryTarget({
                            studiomateMemberId: groupMember.studiomateMemberId,
                            memberName: groupMember.memberName,
                          })
                        }
                      >
                        이력
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Card>

      {saveError && (
        <Alert variant="error" className="mb-4">
          {saveError}
        </Alert>
      )}

      {draftLoaded && (
        <Alert variant="info" className="mb-4">
          임시 저장된 내용을 불러왔습니다.
        </Alert>
      )}

      {/* Section 1: 수업 유형 (자동 판별 + OT 수동 토글) */}
      <Card id="section-class-type" className="p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">수업 유형</h3>
        <ClassTypeBadge classType={classType} isOt={isOt} />
        {classType && (
          <label className="flex items-center gap-2 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isOt}
              onChange={(e) => void handleOtToggle(e.target.checked)}
              className="w-4 h-4"
              aria-label="OT 수업으로 지정"
            />
            <span className="text-sm text-gray-700">OT 수업으로 지정</span>
          </label>
        )}
        {otPrompt && (
          <div className="mt-3 border-t pt-3">
            <p className="text-sm text-gray-600 mb-3">
              {otPrompt.kind === "demote"
                ? `${otPrompt.dateLabel}에 ${otPrompt.instructorName}님이 작성한 ${otPrompt.memberName}님의 OT 일지가 이미 존재합니다. 해당 일지를 일반 일지로 되돌리고 이 일지를 OT 일지로 변경하시겠습니까?`
                : `${otPrompt.memberName}님의 OT 일지는 이 일지 하나뿐입니다. OT를 해제하시겠습니까?`}
            </p>
            <div className="flex gap-3">
              <Button variant="primary" onClick={confirmOtPrompt} className="flex-1">
                확인
              </Button>
              <Button variant="outline" onClick={cancelOtPrompt} className="flex-1">
                취소
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* OT 전용 필드 */}
      {isOt && (
        <Card id="section-ot-fields" className="p-4 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            OT 전용 <span className="text-red-500">*</span>
          </h3>
          <div className="space-y-3">
            {isGroupClass ? (
              // 그룹 수업: 토글 가능한 OT 필드
              <>
                <ToggleableFieldSection
                  label={FIELD_LABELS.instructorMemo}
                  required
                  state={groupForm.toggleFields.instructorMemo}
                  members={groupMembers}
                  placeholder={FIELD_PLACEHOLDERS.instructorMemo}
                  rows={2}
                  onSharedChange={(v) => groupForm.setSharedValue("instructorMemo", v)}
                  onPerMemberChange={(id, v) =>
                    groupForm.setPerMemberValue("instructorMemo", id, v)
                  }
                  onRequestToggle={(mode) => groupForm.requestToggle("instructorMemo", mode)}
                />
                <ToggleableFieldSection
                  label={FIELD_LABELS.bodyAnalysis}
                  required
                  state={groupForm.toggleFields.bodyAnalysis}
                  members={groupMembers}
                  placeholder={FIELD_PLACEHOLDERS.bodyAnalysis}
                  rows={2}
                  onSharedChange={(v) => groupForm.setSharedValue("bodyAnalysis", v)}
                  onPerMemberChange={(id, v) => groupForm.setPerMemberValue("bodyAnalysis", id, v)}
                  onRequestToggle={(mode) => groupForm.requestToggle("bodyAnalysis", mode)}
                />
                <ToggleableFieldSection
                  label={FIELD_LABELS.problemList}
                  required
                  state={groupForm.toggleFields.problemList}
                  members={groupMembers}
                  placeholder={FIELD_PLACEHOLDERS.problemList}
                  rows={3}
                  onSharedChange={(v) => groupForm.setSharedValue("problemList", v)}
                  onPerMemberChange={(id, v) => groupForm.setPerMemberValue("problemList", id, v)}
                  onRequestToggle={(mode) => groupForm.requestToggle("problemList", mode)}
                  sectionId="section-problem-list"
                  renderPerMemberAddon={(memberId) => {
                    const gm = groupMembers.find((m) => m.studiomateMemberId === memberId);
                    return (
                      <PastProblemListPicker
                        ownerUid={centerOwnerUid}
                        centerId={centerId}
                        studiomateMemberId={memberId}
                        memberName={gm?.memberName}
                        onSelect={(text) =>
                          groupForm.setPerMemberValue("problemList", memberId, text)
                        }
                      />
                    );
                  }}
                />
                <ToggleableFieldSection
                  label={FIELD_LABELS.memberGoal}
                  required
                  state={groupForm.toggleFields.memberGoal}
                  members={groupMembers}
                  placeholder={FIELD_PLACEHOLDERS.memberGoal}
                  rows={2}
                  onSharedChange={(v) => groupForm.setSharedValue("memberGoal", v)}
                  onPerMemberChange={(id, v) => groupForm.setPerMemberValue("memberGoal", id, v)}
                  onRequestToggle={(mode) => groupForm.requestToggle("memberGoal", mode)}
                />
              </>
            ) : (
              // 개인 수업: 기존 textarea
              <>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    상담 내용(문진) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={instructorMemo}
                    onChange={(e) => setInstructorMemo(e.target.value)}
                    placeholder="상담 및 문진 내용..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    정·동적 평가 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={bodyAnalysis}
                    onChange={(e) => setBodyAnalysis(e.target.value)}
                    placeholder="정적·동적 평가 내용..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    문제 목록(Problem List) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={problemList}
                    onChange={(e) => setProblemList(e.target.value)}
                    placeholder="고객이 겪는 불편함의 핵심 원인은 무엇이라고 생각하는지를 기록해주세요."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
                  />
                  <PastProblemListPicker
                    ownerUid={centerOwnerUid}
                    centerId={centerId}
                    studiomateMemberId={resolvedStudiomateMemberId}
                    memberName={privateMemberName}
                    onSelect={setProblemList}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    회원 운동 목표 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={memberGoal}
                    onChange={(e) => setMemberGoal(e.target.value)}
                    placeholder="회원이 원하는 운동 목표..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
                  />
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      {/* Section 2+3: 기구 & 수업 시퀀스 (통합) */}
      <EquipmentExerciseSection
        equipments={equipments}
        exercises={exercises}
        allMovements={allMovements ?? []}
        onEquipmentsChange={setEquipments}
        onExercisesChange={setExercises}
      />

      {/* Section 4: 문제 목록 - OT일 때는 OT 전용 섹션에 포함되므로 숨김 */}
      {!isOt && (
        <Card id="section-problem-list" className="p-4 mb-4">
          {isGroupClass ? (
            <ToggleableFieldSection
              label={FIELD_LABELS.problemList}
              required
              state={groupForm.toggleFields.problemList}
              members={groupMembers}
              placeholder={FIELD_PLACEHOLDERS.problemList}
              rows={3}
              onSharedChange={(v) => groupForm.setSharedValue("problemList", v)}
              onPerMemberChange={(id, v) => groupForm.setPerMemberValue("problemList", id, v)}
              onRequestToggle={(mode) => groupForm.requestToggle("problemList", mode)}
              renderPerMemberAddon={(memberId) => {
                const gm = groupMembers.find((m) => m.studiomateMemberId === memberId);
                return (
                  <PastProblemListPicker
                    ownerUid={centerOwnerUid}
                    centerId={centerId}
                    studiomateMemberId={memberId}
                    memberName={gm?.memberName}
                    onSelect={(text) => groupForm.setPerMemberValue("problemList", memberId, text)}
                  />
                );
              }}
            />
          ) : (
            <>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                문제 목록(Problem List) <span className="text-red-500">*</span>
              </h3>
              <textarea
                value={problemList}
                onChange={(e) => setProblemList(e.target.value)}
                placeholder="고객이 겪는 불편함의 핵심 원인은 무엇이라고 생각하는지를 기록해주세요."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
              />
              <PastProblemListPicker
                ownerUid={centerOwnerUid}
                centerId={centerId}
                studiomateMemberId={resolvedStudiomateMemberId}
                memberName={privateMemberName}
                onSelect={setProblemList}
              />
            </>
          )}
        </Card>
      )}

      {/* Section 5: 특이사항 */}
      <Card className="p-4 mb-4">
        {isGroupClass ? (
          <ToggleableFieldSection
            label={FIELD_LABELS.notes}
            state={groupForm.toggleFields.notes}
            members={groupMembers}
            placeholder={FIELD_PLACEHOLDERS.notes}
            rows={2}
            onSharedChange={(v) => groupForm.setSharedValue("notes", v)}
            onPerMemberChange={(id, v) => groupForm.setPerMemberValue("notes", id, v)}
            onRequestToggle={(mode) => groupForm.requestToggle("notes", mode)}
          />
        ) : (
          <>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">특이사항 (선택)</h3>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="통증, 컨디션, 다음 계획..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
            />
          </>
        )}
      </Card>

      {/* Section 6: 개선된 점 */}
      <Card id="section-improvements" className="p-4 mb-4">
        {isGroupClass ? (
          <ToggleableFieldSection
            label={FIELD_LABELS.improvements}
            required
            state={groupForm.toggleFields.improvements}
            members={groupMembers}
            placeholder={FIELD_PLACEHOLDERS.improvements}
            rows={2}
            onSharedChange={(v) => groupForm.setSharedValue("improvements", v)}
            onPerMemberChange={(id, v) => groupForm.setPerMemberValue("improvements", id, v)}
            onRequestToggle={(mode) => groupForm.requestToggle("improvements", mode)}
          />
        ) : (
          <>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              개선된 점 <span className="text-red-500">*</span>
            </h3>
            <textarea
              value={improvements}
              onChange={(e) => setImprovements(e.target.value)}
              placeholder="개선된 점을 기록해주세요..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
            />
          </>
        )}
      </Card>

      {/* Section 7: 개선해야할 점 */}
      <Card id="section-improvements-needed" className="p-4 mb-4">
        {isGroupClass ? (
          <ToggleableFieldSection
            label={FIELD_LABELS.improvementsNeeded}
            required
            state={groupForm.toggleFields.improvementsNeeded}
            members={groupMembers}
            placeholder={FIELD_PLACEHOLDERS.improvementsNeeded}
            rows={2}
            onSharedChange={(v) => groupForm.setSharedValue("improvementsNeeded", v)}
            onPerMemberChange={(id, v) => groupForm.setPerMemberValue("improvementsNeeded", id, v)}
            onRequestToggle={(mode) => groupForm.requestToggle("improvementsNeeded", mode)}
          />
        ) : (
          <>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              개선해야할 점 <span className="text-red-500">*</span>
            </h3>
            <textarea
              value={improvementsNeeded}
              onChange={(e) => setImprovementsNeeded(e.target.value)}
              placeholder="개선해야할 점을 기록해주세요..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-primary-400 resize-none"
            />
          </>
        )}
      </Card>

      {/* Validation hint */}
      {!isFormValid && missingFields.length > 0 && (
        <p className="text-xs text-gray-400 mb-3 text-center">
          미입력 항목: {missingFields.join(", ")}
        </p>
      )}

      {/* Manual no-show / cancel no-show */}
      {currentIsNoShow ? (
        showCancelNoShowConfirm ? (
          <Card className="p-4 mb-4">
            <p className="text-sm text-gray-600 mb-4">노쇼 처리를 취소하시겠습니까?</p>
            <div className="flex gap-3">
              <Button
                variant="primary"
                onClick={() => cancelNoShowMutation.mutate()}
                disabled={cancelNoShowMutation.isPending}
                className="flex-1"
              >
                {cancelNoShowMutation.isPending ? "처리 중..." : "확인"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCancelNoShowConfirm(false)}
                className="flex-1"
              >
                취소
              </Button>
            </div>
          </Card>
        ) : (
          <Button
            variant="outline"
            onClick={() => setShowCancelNoShowConfirm(true)}
            className="w-full mb-3"
          >
            노쇼처리 취소
          </Button>
        )
      ) : showNoShowConfirm ? (
        <Card className="p-4 mb-4">
          <p className="text-sm text-gray-600 mb-4">
            노쇼로 처리하시겠습니까? 일지 없이 수업이 완료 처리됩니다.
          </p>
          <div className="flex gap-3">
            <Button
              variant="primary"
              onClick={() => noShowMutation.mutate()}
              disabled={noShowMutation.isPending}
              className="flex-1"
            >
              {noShowMutation.isPending ? "처리 중..." : "확인"}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowNoShowConfirm(false)}
              className="flex-1"
            >
              취소
            </Button>
          </div>
        </Card>
      ) : (
        <Button
          variant="outline"
          onClick={() => setShowNoShowConfirm(true)}
          className="w-full mb-3"
        >
          노쇼로 처리
        </Button>
      )}

      {/* Draft save button (개인 수업만) */}
      {!isGroupClass && (
        <>
          <Button variant="outline" onClick={handleDraftSave} className="w-full mb-3">
            임시 저장
          </Button>
          {draftSaveSuccess && (
            <p className="text-xs text-green-600 text-center mb-3">임시 저장되었습니다.</p>
          )}
        </>
      )}

      {/* Save button */}
      <Button
        variant="primary"
        size="lg"
        onClick={handleSave}
        disabled={isSaving}
        className="w-full"
      >
        {isSaving ? "저장 중..." : hasExistingData ? "일지 수정" : "일지 저장"}
      </Button>

      <JournalHistoryDrawer
        isOpen={historyTarget !== null}
        onClose={() => setHistoryTarget(null)}
        ownerUid={centerOwnerUid}
        centerId={centerId}
        studiomateMemberId={historyTarget?.studiomateMemberId}
        memberName={historyTarget?.memberName}
      />
    </AppLayout>
  );
}
